import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

const configNode = document.getElementById('BazConversionConfig');
const config = JSON.parse(configNode.textContent);
const decode = text => new DOMParser().parseFromString(text || '', 'text/html').body.textContent;
const copy = Object.fromEntries(Object.entries(config.text).map(([key,value]) => [key,decode(value)]));
const isKuwaitMarket = () => config.shippingMarket === 'kw';
const root = config.root.replace(/\/$/,'') + '/';
const rate = () => config.currency === 'KWD' ? 1 : (Number(window.Shopify?.currency?.rate) || 1);
const threshold = () => Math.round(config.threshold * 100 * rate());
const nativeMoney = amount => {
  const precision = new Intl.NumberFormat('en',{style:'currency',currency:config.currency}).resolvedOptions().maximumFractionDigits;
  const format = config.moneyFormat.replace(/(}})0(?=\s|<|$)/g,'$1');
  return decode(formatMoney(amount * Math.pow(10, precision) / 100, format, config.currency));
};
const shippingMoney = amount => {
  return new Intl.NumberFormat(config.locale,{numberingSystem:'latn',maximumFractionDigits:3}).format(amount/100) + (config.locale === 'ar' ? ' د.ك' : ' KD');
};
const shippingCopy = (key,total=0) => (copy[key] || '').replaceAll('[fee]',shippingMoney(Math.round((isKuwaitMarket()?config.feeKW:config.feeGCC)*100)))
  .replaceAll('[fee_phrase]',Number(config.feeKW)===1?'بدينار واحد':'بـ'+config.feeKW+' دينار').replaceAll('[threshold_number]',String(config.threshold)).replaceAll('[threshold]',shippingMoney(config.threshold*100)).replaceAll('[remaining]',shippingMoney(Math.max(0,threshold()-total)/rate()))
  .replaceAll('[country]',copy['country_'+config.country] || config.countryName || config.country);
let cartRequest;
const getCart = () => cartRequest ||= fetch(root+'cart.js',{headers:{Accept:'application/json'}}).then(r=>{if(!r.ok)throw Error('Cart unavailable');return r.json();}).finally(()=>{cartRequest=null;});
let timer;
async function refreshCart(event) {
  try { if(event?.promise) await event.promise; } catch { return; }
  clearTimeout(timer);
  timer=setTimeout(async()=>{
    try {
      const cart=await getCart();
      document.querySelectorAll('baz-shipping').forEach(el=>el.update(cart.total_price));
      document.querySelectorAll('baz-cart-upsell').forEach(el=>el.update(cart));
    }catch(error){console.warn('[BAZ delivery]',error.message);}
  },80);
}
document.addEventListener(StandardEvents.cartLinesUpdate,refreshCart);
document.addEventListener(StandardEvents.cartDiscountUpdate,refreshCart);

class BazShipping extends Component {
  connectedCallback(){super.connectedCallback();this.update(Number(this.dataset.total));}
  update(total){
    const kw=this.dataset.shippingMarket==='kw',free=total>=threshold();
    const compact=this.dataset.compact==='true';
    const key=kw ? (free?'free':compact?'fee':'remaining') : 'gcc';
    const text=this.querySelector('[data-shipping-message]');
    if(text)text.textContent=shippingCopy(key,total);
    const progress=this.querySelector('progress');
    if(progress){progress.hidden=!kw;progress.max=Math.max(1,threshold());progress.value=Math.min(total,threshold());progress.setAttribute('aria-label',shippingCopy('card'));}
    const fee=this.querySelector('[data-shipping-fee]');
    if(fee){fee.hidden=!kw||free;fee.textContent=shippingCopy('fee');}
    this.dataset.total=String(total);
  }
}
customElements.define('baz-shipping',BazShipping);

const recommendationCache=new Map();
async function recommendations(id){
  if(!recommendationCache.has(id))recommendationCache.set(id,(async()=>{
    const url=new URL(root+'recommendations/products.json',location.origin);
    url.searchParams.set('product_id',id);url.searchParams.set('limit','4');
    const result=[];
    for(const intent of ['complementary','related']){
      url.searchParams.set('intent',intent);
      const response=await fetch(url);if(!response.ok)continue;
      const data=await response.json();result.push(...(data.products||[]));
    }
    return [...new Map(result.map(p=>[p.id,p])).values()];
  })().catch(error=>{recommendationCache.delete(id);throw error;}));
  return recommendationCache.get(id);
}

class BazCartUpsell extends Component {
  #abort;#observer;#visible=false;#revision=0;#cart;
  connectedCallback(){
    super.connectedCallback();this.#abort=new AbortController();
    this.#cart={total_price:Number(this.dataset.total),items:this.dataset.cartProducts.split(',').filter(Boolean).map(id=>({product_id:Number(id)}))};
    this.addEventListener('click',this.#add,{signal:this.#abort.signal});
    this.#observer=new IntersectionObserver(entries=>{if(entries.some(e=>e.isIntersecting)){this.#visible=true;this.#render();this.#observer.disconnect();}},{rootMargin:'150px'});
    this.#observer.observe(this);
  }
  disconnectedCallback(){super.disconnectedCallback();this.#abort?.abort();this.#observer?.disconnect();this.#revision++;}
  update(cart){this.#cart=cart;if(this.#visible)this.#render();}
  async #render(){
    const id=this.#cart.items[0]?.product_id;if(!id)return;
    const revision=++this.#revision;
    try{
      const products=await recommendations(String(id));if(!this.isConnected||revision!==this.#revision)return;
      const inCart=new Set(this.#cart.items.map(i=>Number(i.product_id)));
      const remaining=Math.max(0,threshold()-this.#cart.total_price);
      let candidates=products.filter(p=>p.available&&!inCart.has(p.id)).map(p=>({product:p,variant:p.variants.find(v=>v.available)})).filter(p=>p.variant);
      if(isKuwaitMarket()&&remaining>0)candidates.sort((a,b)=>Math.abs(a.variant.price-remaining)-Math.abs(b.variant.price-remaining));
      const fragment=document.createDocumentFragment();
      for(const {product,variant} of candidates.slice(0,3)){
        const row=document.createElement('article');row.className='baz-upsell-card';
        const url=new URL(product.url,location.origin);url.searchParams.set('variant',String(variant.id));
        const imageLink=document.createElement('a');imageLink.href=url.href;imageLink.tabIndex=-1;imageLink.setAttribute('aria-hidden','true');
        const src=variant.featured_image?.src||product.featured_image||product.images?.[0];
        if(src){const img=document.createElement('img');const imageUrl=new URL(src,location.origin);imageUrl.searchParams.set('width','144');img.src=imageUrl.href;img.alt='';img.width=64;img.height=76;img.loading='lazy';imageLink.append(img);}
        const info=document.createElement('div');info.className='baz-upsell-info';
        const title=document.createElement('a');title.href=url.href;title.className='baz-upsell-title';title.textContent=product.title;info.append(title);
        const price=document.createElement('bdi');price.dir='ltr';price.textContent=window.BazDisplayCurrency?.formatAmount(variant.price)||nativeMoney(variant.price);info.append(price);
        if(isKuwaitMarket()&&remaining>0&&variant.price>=remaining){const label=document.createElement('small');label.textContent=copy.qualifies;info.append(label);}
        const button=document.createElement('button');button.type='button';button.className='baz-upsell-add';button.dataset.variantId=variant.id;button.textContent=copy.add;button.setAttribute('aria-label',copy.add+' '+product.title);
        row.append(imageLink,info,button);fragment.append(row);
      }
      this.querySelector('.baz-upsell-list').replaceChildren(fragment);this.classList.toggle('baz-upsell-empty',!candidates.length);
    }catch{this.classList.add('baz-upsell-empty');}
  }
  #add=async event=>{
    const button=event.target.closest('button[data-variant-id]');if(!button||button.disabled)return;
    button.disabled=true;button.textContent=copy.adding;
    const error=this.querySelector('.baz-upsell-error');error.hidden=true;
    const sections=[...new Set([...document.querySelectorAll('cart-items-component')].map(el=>el.dataset.sectionId).filter(Boolean))];
    const deferred=CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({action:'add',context:'cart',lines:[{merchandiseId:button.dataset.variantId,quantity:1}],promise:deferred.promise}));
    try{
      const response=await fetch(root+'cart/add.js',{method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({items:[{id:Number(button.dataset.variantId),quantity:1}],sections,sections_url:location.pathname})});
      const result=await response.json();if(!response.ok||result.status)throw Error(result.description||copy.error);
      const cart=await getCart();
      deferred.resolve({cart:CartLinesUpdateEvent.createCartFromAjaxResponse(cart),detail:{sections:result.sections,items:cart.items,itemCount:1,source:'baz-cart-upsell',didError:false}});
      button.textContent=copy.added;
    }catch(err){deferred.reject(err);error.textContent=err.message||copy.error;error.hidden=false;button.disabled=false;button.textContent=copy.add;}
  };
}
customElements.define('baz-cart-upsell',BazCartUpsell);

// Only money generated by this feature is updated here. Native product prices
// continue to follow the theme's variant renderer and currency formatter.
document.querySelectorAll('[data-baz-delivery-copy]').forEach(el=>{
  const key=el.dataset.bazDeliveryCopy==='trust'&&!isKuwaitMarket()?'gcc':el.dataset.bazDeliveryCopy;
  el.textContent=shippingCopy(key);
});
function hideSparseBadges(root=document){
  root.querySelectorAll('.jdgm-preview-badge[data-number-of-reviews]').forEach(badge=>{badge.hidden=Number(badge.dataset.numberOfReviews)<3;});
}
const badgeObservers=new WeakMap();
function watchBadges(root=document){
  hideSparseBadges(root);
  root.querySelectorAll('.jdgm-preview-badge').forEach(badge=>{
    if(badgeObservers.has(badge))return;
    const update=()=>{const count=badge.dataset.numberOfReviews||badge.querySelector('[data-number-of-reviews]')?.dataset.numberOfReviews;if(count!==undefined)badge.hidden=Number(count)<3;};
    const observer=new MutationObserver(update);
    observer.observe(badge,{childList:true,subtree:true,attributes:true,attributeFilter:['data-number-of-reviews']});badgeObservers.set(badge,observer);update();
  });
}
watchBadges();document.addEventListener('shopify:section:load',e=>watchBadges(e.target));

// Keep Shopify's native consent signals; defer only the non-critical recorder.
if(config.clarityId && /^[a-z0-9]+$/i.test(config.clarityId)){
  const interaction=new AbortController();
  const startClarity=()=>{
    interaction.abort();
    if(document.querySelector('script[src^="https://www.clarity.ms/tag/"]'))return;
    window.clarity=window.clarity||function(){(window.clarity.q=window.clarity.q||[]).push(arguments);};
    const consent=()=>window.clarity('consentv2',{
      ad_Storage:window.Shopify?.customerPrivacy?.marketingAllowed()?'granted':'denied',
      analytics_Storage:window.Shopify?.customerPrivacy?.analyticsProcessingAllowed()?'granted':'denied',source:101
    });
    consent();
    window.Shopify?.loadFeatures?.([{name:'consent-tracking-api',version:'0.1'}],error=>{if(!error)consent();});
    document.addEventListener('visitorConsentCollected',consent);
    const script=document.createElement('script');script.async=true;script.src='https://www.clarity.ms/tag/'+config.clarityId+'?ref=shopify';document.head.append(script);
  };
  for(const name of ['pointerdown','keydown','scroll'])window.addEventListener(name,startClarity,{once:true,passive:true,signal:interaction.signal});
}

// Measure the actual navigation height, including the iPhone safe area.
const nav=document.querySelector('[data-baz-mobile-nav]');
if(nav){const resize=new ResizeObserver(()=>document.documentElement.style.setProperty('--baz-mobile-nav-offset',nav.getBoundingClientRect().height+'px'));resize.observe(nav);window.addEventListener('pagehide',()=>resize.disconnect(),{once:true});}


